/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState, useEffect, useMemo } from 'react';

// react-router components
import { Route, useLocation, Routes, Navigate } from 'react-router-dom';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';

// Vision UI Dashboard PRO React example components
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';

// Vision UI Dashboard PRO React themes
import theme from 'assets/theme';
import themeRTL from 'assets/theme/theme-rtl';

// RTL plugins
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Vision UI Dashboard PRO React routes
import routes from './routes';

// Vision UI Dashboard PRO React contexts
import {
  useVisionUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from 'context';
import { RouteProvider } from 'context/RouteContext';
import { AuthProvider } from 'context/AuthContext';
import ProtectedRoute from 'components/ProtectedRoute';

// Plugins custom css
import 'assets/theme/base/plugins.css';

export default function App() {
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (routes) => {
    return routes.map((route, key) => {
      if (route.layout === '/admin' || route.layout === '') {
        // Check if route requires authentication (not a public auth page)
        const isAuthRoute = route.path && (
          route.path.includes('/sign-in') || 
          route.path.includes('/sign-up') ||
          route.path.includes('/authentication/')
        );
        
        // Wrap protected routes with ProtectedRoute
        const element = isAuthRoute ? route.component : (
          <ProtectedRoute>{route.component}</ProtectedRoute>
        );
        
        return (
          <Route path={`${route.path}`} element={element} key={key} />
        );
      }
      if (route.collapse) {
        return getRoutes(route.collapse);
      } else {
        return null;
      }
    });
  };


  return (
    <AuthProvider>
      <RouteProvider>
        {direction === 'rtl' ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={themeRTL}>
            <CssBaseline />
            {layout === '/admin' && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brandName="PRISM"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
              </>
            )}
            {layout === 'vr' && <Configurator />}
            <Routes>
              {getRoutes(routes)}
              <Route
                path="/"
                element={<ProtectedRoute><Navigate to="/dashboards/overview" replace /></ProtectedRoute>}
              />
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {layout === 'dashboard' && (
            <>
              <Sidenav
                color={sidenavColor}
                brandName="PRISM"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
            </>
          )}
          {layout === 'vr' && <Configurator />}
            <Routes>
              {getRoutes(routes)}
              <Route
                path="/"
                element={<ProtectedRoute><Navigate to="/dashboards/overview" replace /></ProtectedRoute>}
              />
            </Routes>
        </ThemeProvider>
        )}
      </RouteProvider>
    </AuthProvider>
  );
}
